import { CountAddItem, CountAddProps, CountOrder, WLocation } from "@/types/inventoryV2";
import { Button, Flex, Modal, Spin, Upload, App, notification, Progress, Checkbox, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { FC, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import { generateTemple, parseExcelFile, ImportCountTemple } from "./utils";
import SText from "@/styles/SText";
import { addLocation, locationList, countAdd as countAddApi, countDo } from "@/apis/inventoryv2";
import { getLocationIdx } from "@/utils/wLocation";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";
import { Product } from "@/types/product";
import { productList } from "@/apis/product";
import { generateLotV2 } from "@/utils/codeParse";
import { plus } from "@/utils/calc";
import { numFormmater } from "@/pages/Requirement/util";

interface BatchImportProps {
  onSuccess?: (orders: CountOrder[]) => void
}

interface ValidRowData extends ImportCountTemple {
  location?: WLocation;
  product?: Product;
};

interface ReadyItem extends CountAddItem {
  product?: Product;
}
interface ReadyData extends CountAddProps {
  location?: WLocation;
  items: Array<ReadyItem>;
  orderNumber?: string;
  passed?: boolean;
}

const BatchImport: FC<BatchImportProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [open, setOpen] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [readyData, setReadyData] = useState<ReadyData[]>([]);
  const [processed, setProcessed] = useState<number>(0);
  const [allNum, setAllNum] = useState<number>(0);
  const [noLotChecked, setNoLotChecked] = useState<0 | 1>(0);
  const [isPreData, setIsPreData] = useState<boolean>(false);
  const [isPassed, setIsPassed] = useState<boolean>(false);
  const [desc, setDesc] = useState<string>('');

  const countAdd = async () => {
    if (readyData.length <= 0) return notification.error({ placement: 'top', message: t('bom.errTip') })

    setLoading(true);
    let allPass = true;
    let orders: CountOrder[] = [];
    try {
      let readyTemp = [...readyData];
      setAllNum(readyTemp.length);
      setProcessed(0);
      for (let i = 0; i < readyTemp.length; i++) {
        const addData = readyTemp[i];
        try {
          let countOrder = await countAddApi({
            locationId: addData.locationId,
            desc,
            items: addData.items.map(item => ({
              lotStr: item.lotStr,
              productCode: item.productCode,
              counting: item.counting,
              barcodes: item.barcodes,
            }))
          });
          if (countOrder) {
            readyTemp[i].orderNumber = countOrder.number;
            try {
              countOrder = await countDo(countOrder.id, 1);
              readyTemp[i].passed = true;
            } catch (error) {
              readyTemp[i].passed = false;
              allPass = false
            }
            orders.push(countOrder);
          } else readyTemp[i].passed = false;

        } catch (error) {
          readyTemp[i].passed = false;
          allPass = false;
        }
        setProcessed(i + 1);
      }
    } catch (error) {
      notification.error({ message: t('counting.countErr') })
      setLoading(false);
      return
    }
    setLoading(false);
    setIsPassed(allPass);
    if (allPass) {
      notification.success({ message: t('counting.importSuccess') })
      onSuccess?.(orders);
    } else {
      notification.warning({ message: t('counting.countErrTip'), description: t('counting.countErrTip1') })
    }
  }

  // 表格列定义
  const columns: ColumnsType<ReadyData> = [
    {
      title: t('counting.countNo'),
      key: 'orderNumber',
      width: 120,
      render: (_, row) => (<SText style={{ color: row.passed ? "green" : "red" }} text={row.orderNumber ?? '-'} />)
    },
    {
      title: t('counting.locationIdx'),
      key: 'locationIdx',
      width: 120,
      sorter: (a, b) => {
        let aIdx = getLocationIdx(a.location);
        let bIdx = getLocationIdx(b.location);
        return aIdx.localeCompare(bIdx);
      },
      render: (_, row) => (<SText text={getLocationIdx(row.location)} />)
    },
    {
      title: t('warehouse.label1'),
      dataIndex: 'label1',
      key: 'label1',
      width: 120,
      render: (_, row) => (<SText text={row.location?.label1 ?? ''} />)
    },
    {
      title: t('counting.productCode'),
      key: 'productCode',
      width: 120,
      render: (_, row) => (
        row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={item.productCode} /></Flex>)
      )
    },
    {
      title: t('counting.productName'),
      key: 'productName',
      width: 150,
      render: (_, row) => (
        row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={item.product?.name ?? ''} /></Flex>)
      )
    },
    {
      title: t('counting.productModal'),
      key: 'productModal',
      render: (_, row) => (
        row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={item.product?.model ?? ''} /></Flex>)
      )
    },
    {
      title: t('counting.productUom'),
      key: 'productUom',
      width: 80,
      render: (_, row) => (
        row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={item.product?.uom ?? ''} /></Flex>)
      )
    },
    // {
    //   title: t('counting.bookNum'),
    //   dataIndex: 'bookNum',
    //   key: 'bookNum',
    //   width: 100,
    //   render: (_, row) => (
    //     row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={item.counting?? ''} /></Flex>)
    //   )
    // },
    {
      title: "lot",
      key: 'lot',
      width: 100,
      render: (_, row) => (
        row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={item.lotStr} /></Flex>)
      )
    },
    {
      title: t('counting.quantity'),
      key: 'countedNum',
      width: 100,
      render: (_, row) => (
        row.items.map(item => <Flex style={{ marginTop: 2 }}><SText text={numFormmater(Number(item.counting))} /></Flex>)
      )
    }
  ];


  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      //解析数据原始结果
      let parseResult: ValidRowData[] = await parseExcelFile(file, t);
      // 验证无效数据
      let errRow = parseResult.filter(row => !row.hasContent).map(row => row.rowIndex);
      if (!!errRow.length) {
        notification.error({
          message: t('counting.countErrTip2'),
          description: (
            <Fragment>
              <div>{t('counting.countErrTip3')}</div>
              {
                errRow.map(idx => <span>{t('counting.countErrTip7', { index: idx })}, </span>)
              }
            </Fragment>
          )
        })
        setUploading(false);
        return false;
      }
      //去重检查库位、料号
      let productCodes: string[] = [];
      let locationIdxs: Array<{ locationIdx: string, label1?: string }> = [];
      parseResult.forEach(row => {
        if (!productCodes.includes(row.productCode)) productCodes.push(row.productCode);
        if (!!!locationIdxs.find(loc => row.locationIdx === loc.locationIdx)) locationIdxs.push({ locationIdx: row.locationIdx, label1: row.label1 });
      });
      //请求料号
      const productListRes = await productList({ params: { strs: productCodes } });
      const codeMap: Record<string, Product> = {};
      (productListRes?.body || []).forEach(p => codeMap[p.code] = { ...p });
      //验证是否有物料不存在
      let failCodes = productCodes.filter(code => !!!codeMap[code]);
      if (!!failCodes.length) {
        notification.error({
          message: t('counting.countErrTip4'),
          description: failCodes.join(', '),
        });
        setUploading(false);
        return false;
      };
      //请求库位
      let locationMap: Record<string, WLocation> = {};
      for (let i = 0; i < locationIdxs.length; i++) {
        let loc = locationIdxs[i];
        let location: WLocation | null = null;
        location = (await locationList({ body: loc.locationIdx }))?.body?.[0] || null;
        if (!!location) locationMap[loc.locationIdx] = { ...location }
        else {
          let [l1, l2, l3, l4, l5] = loc.locationIdx.split("-");
          location = await addLocation({
            l1: Number(l1),
            l2: Number(l2) ?? null,
            l3: Number(l3) ?? null,
            l4: Number(l4) ?? null,
            l5: Number(l5) ?? null,
            label1: loc.label1,
            type: 1
          });
          locationMap[loc.locationIdx] = { ...location };
        }
      }
      //初始数据赋值料号\库位&&完善readyData
      let readyList: ReadyData[] = [];
      let now = Date.now();
      for (let index = 0; index < parseResult.length; index++) {
        let locationIdx = parseResult[index].locationIdx;
        let productCode = parseResult[index].productCode;
        let locData = locationMap[locationIdx];
        let productData = codeMap[productCode];
        // parseResult[index]["location"] = locationMap[locationIdx];
        // parseResult[index]["product"] = codeMap[productCode];

        let lotStr = generateLotV2(codeMap[productCode]?.code ?? '', '', now++);
        let findReayIdx = readyList.findIndex(ready => ready.locationId === locData.id);
        if (findReayIdx > 0) {
          let findReayItemIdx = readyList[findReayIdx].items.findIndex(ready => ready.productCode === productCode);
          if (findReayItemIdx > 0) {
            let counting = readyList[findReayIdx].items[findReayItemIdx].counting
            readyList[findReayIdx].items[findReayItemIdx].counting = plus(counting, parseResult[index].countedNum!)
          } else readyList[findReayIdx].items.push({
            lotStr,
            productCode,
            counting: parseResult[index].countedNum!,
            barcodes: [],
            product: productData
          })
        } else {
          readyList.push({
            locationId: locationMap[locationIdx].id,
            desc: '',
            items: [{
              lotStr,
              productCode,
              counting: parseResult[index].countedNum!,
              barcodes: [],
              product: productData
            }],
            location: locData,
          })
        }
      };
      setReadyData([...readyList]);
      setIsPreData(false);
    } catch {
      notification.error({
        placement: 'top',
        message: t('counting.countErrTip5'),
        description: t('counting.countErrTip6'),
      });
    } finally {
      setUploading(false);
      setIsPreData(false);
    }
    return false; // 阻止自动上传
  };

  // 关闭Modal时的确认
  const handleCancel = () => {
    if (readyData.length > 0) {
      modal.confirm({
        title: t('counting.confirmCloseImport'),
        content: t('counting.confirmCloseImportContent'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        onOk: () => {
          setReadyData([]);
          setDesc('');
          setOpen(false);
        },
      });
    } else {
      setDesc('');
      setOpen(false);
    }
  };

  return (
    <>
      <Button onClick={() => { setOpen(true); setIsPassed(false); setAllNum(0) }}>{t('counting.batchImport')}</Button>
      <Modal open={open} onCancel={handleCancel} maskClosable={false} width={1200}
        centered
        title={
          <Flex gap={10} align="center" justify="flex-start" >
            <SText text={t('counting.startInv')} style={{ width: "unset" }} />
            {/* <Checkbox
              checked={noLotChecked === 1}
              onChange={(e) => setNoLotChecked(e.target.checked ? 1 : 0)}
            >
              {t('counting.noLot')}
            </Checkbox> */}
          </Flex >
        }

        okButtonProps={{ loading: loading || uploading, disabled: isPassed }}
        cancelButtonProps={{ disabled: loading || uploading }}
        closable={!loading && !uploading}
        okText={t('counting.confirmImport')}
        onOk={() => countAdd()}
      >
        <Flex vertical style={{ width: "100%", height: "70vh", marginBottom: -34 }} >
          <Flex justify="space-between" style={{ marginBottom: 10 }}>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={uploading}
            >
              <Button type="primary" loading={uploading}>{t('counting.importData')}</Button>
            </Upload>
            <Button type="link" onClick={() => generateTemple(t)}>{t('counting.downloadTemplate')}</Button>
          </Flex>
          <Flex vertical gap={6} style={{ marginBottom: 20 }}>
            <span>{t('purchase.remark')}</span>
            <Input.TextArea
              rows={2}
              value={desc}
              placeholder={t('counting.inputDesc')}
              onChange={(e) => setDesc(e.target.value)}
              maxLength={500}
              showCount
            />
          </Flex>
          <STableLayout
            columns={columns as Array<AnyObject>}
            dataSource={readyData}
            headerChildren={
              allNum && <Progress status={isPassed ? "success" : "normal"} percent={Number((processed / allNum * 100).toFixed(2))} />
            }
            total={readyData.length}
            pagination={false}
            extHeight={70}
          // onRow={(record) => ({
          //   style: {
          //     backgroundColor:
          //       record.passed === false
          //         ? '#fff1f0'
          //         : record.passed === true
          //           ? '#f6ffed'
          //           : undefined,
          //   },
          // })}
          />
        </Flex>
      </Modal>
      {
        (uploading) && <Modal title={t('counting.batchImport') + "..."} closable={false} footer={null} maskClosable={false} open={loading} centered width={1000} >
          <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center" >
            <Spin spinning={loading} size="large" />
          </Flex>
          {
            isPreData ? <span>{t('counting.initLoc')}</span>
              : <>
                <Progress percent={Number((processed / allNum * 100).toFixed(2))} />
                <span>{`${t('counting.batchImport')}：${processed}/${allNum}`}</span>
              </>
          }
        </Modal>
      }
    </>
  )
};

export default BatchImport;
